/**
 * useClawWs
 *
 * 职责划分：
 *  - Store (useClawWsStore)  持久化状态：连接状态、会话列表、当前会话、消息流
 *  - Hook  (useClawWs)       WS 生命周期 + 封装常用方法
 *
 * 客户端实例采用模块级单例，跨路由导航不重复创建，只有第一次调用才真正连接。
 */

import { useCallback, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ClawWsClient, type RawFrameHandler } from '@src/services/claw-ws';
import type {
  Attachment,
  ChatEventPayload,
  ChatSendParams,
  EventPayloads,
  GatewayEvent,
} from '@src/services/claw-ws';
import type {
  AgentEventPayload,
  ToolData,
  ToolStartData,
  ToolResultData,
} from '@src/services/claw-ws/protocol';
import useClawWsStore, {
  isMarioWebchatSessionKey,
} from '@src/stores/useClawWsStore';
import type { ClawChatMessage, ClawSession } from '@src/stores/useClawWsStore';
import useClawStatus, { ClawStatus } from '@src/stores/useClawStatus';
import { sessionsSummarize } from '@src/services/claw/api';
import { MAINAGENT_ROUTE_BASE } from '@src/utils/mainagent-route';

// ─── WS 地址配置 ───────────────────────────────────────────────────────────────
// 开发环境通过 rsbuild proxy /claw-ws → ws://163.7.5.215:18789 解决跨域

const WS_URL =
  process.env.NODE_ENV === 'production'
    ? `wss://${window.location.hostname}/claw/api/ws`
    : '/claw-ws';

// ─── 模块级单例 ────────────────────────────────────────────────────────────────
// 放模块作用域而非 ref，确保多个组件共享同一个连接

let _client: ClawWsClient | null = null;
// 与 _client 同级，确保多个组件实例挂载/卸载时不会重复注册事件监听器
let _listenersRegistered = false;
/**
 * 正在进行中的 run 的 idempotencyKey 集合。
 */
const _activeRuns = new Set<string>();

/** 当前页面生命周期内由 sendMessage 新建的 session base key 集合。
 *  新建 session 不需要拉历史消息，用此集合让 MessageList 跳过 fetchHistory。
 *  页面刷新后集合重置，届时历史接口已有数据，正常拉取。
 */
const freshSessionBaseKeys = new Set<string>();

/** 正在自动生成标题的 session key 集合，防止重复触发 */
const _summarizingKeys = new Set<string>();

/** 正在刷新会话列表的 base key 集合，防止同一未知会话多条消息触发重复拉取 */
const _pendingSessionRefreshKeys = new Set<string>();

/** 提取消息内容纯文本（兼容 string 和 ContentBlock[] 两种格式） */
function extractFirstUserContent(content: unknown): string {
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    return content
      .filter((b) => (b as { type: string }).type === 'text')
      .map((b) => (b as { text?: string }).text ?? '')
      .join('');
  }
  return '';
}

/**
 * 为没有 label 的会话自动生成标题：
 * 拉取历史 → 取第一条 user 消息 → summarize → sessions.patch
 */
async function autoLabelSession(sessionKey: string): Promise<void> {
  if (_summarizingKeys.has(sessionKey)) return;
  _summarizingKeys.add(sessionKey);

  try {
    const client = getOrCreateClient();
    const result = await client.request('chat.history', {
      sessionKey,
      limit: 50,
    });
    const messages =
      (result as { messages?: Array<{ role: string; content: unknown }> })
        .messages ?? [];

    const firstUser = messages.find((m) => m.role === 'user');
    if (!firstUser) return;

    const content = extractFirstUserContent(firstUser.content);
    if (!content.trim()) return;

    const res = await sessionsSummarize({ content, size: 30 });
    const title = res.data?.title;
    if (!title) return;

    await client.request('sessions.patch', { key: sessionKey, label: title });

    useClawWsStore
      .getState()
      .setSessions(
        useClawWsStore
          .getState()
          .sessions.map((s) =>
            s.key === sessionKey ? { ...s, label: title } : s,
          ),
      );
  } catch {
    // 静默忽略，不影响正常流程
  } finally {
    _summarizingKeys.delete(sessionKey);
  }
}

export const getBaseKeyModule = (key: string) =>
  (key.split(':').pop() ?? key).replace(/\//g, '-');

/**
 * 构造与网关一致的按 Agent 维度的 sessionKey（默认 main 为 `agent:main:<uuid>`）。
 * 与服务端约定为 `agent:<agentId>:<会话末段>`，末段一般为新建时的 UUID，或与历史会话对齐的 base key。
 */
export function buildClawAgentSessionKey(
  agentId: string,
  sessionBaseKey?: string,
): string {
  const base = sessionBaseKey?.trim() || uuidv4();
  return `agent:${agentId}:${base}`;
}

/** 解析 claw agent session key (agent:<main | [agentId]>:<sessionBaseKey>)，返回 agentId 和 sessionBaseKey */
export function parseClawAgentSessionKey(sessionKey: string): {
  agentId?: 'main' | ({} & string);
  sessionBaseKey?: string;
} {
  const [, agentId, sessionBaseKey] = sessionKey.split(':');
  return {
    agentId,
    sessionBaseKey,
  };
}

/** 与聊天无关的 /mainagent/:first 段（与路由约定一致），不应按会话 UUID 高亮历史列表 */
const CLAW_NON_CHAT_PATH_SEGMENTS = new Set(['dashboard', 'chat', 'workbench']);

/**
 * 当前地址若为「某条 Claw 聊天会话」页（/mainagent/:sessionBase），返回 URL 中的 base；否则 null。
 * /mainagent、/mainagent/dashboard、/skills 等均返回 null。
 * 2026-04 页面主路径从 /claw 切到 /mainagent，旧 /claw 的兼容跳转在路由层处理。
 */
export function getClawSessionUrlBaseKey(pathname: string): string | null {
  const p = pathname.replace(/\/$/, '') || '/';
  if (!p.startsWith(`${MAINAGENT_ROUTE_BASE}/`)) return null;
  const seg = p.slice(`${MAINAGENT_ROUTE_BASE}/`.length).split('/')[0] ?? '';
  if (!seg || CLAW_NON_CHAT_PATH_SEGMENTS.has(seg)) return null;
  return seg;
}

/** 判断某 session 是否为当前页面生命周期内新建的（无需拉历史） */
export function isFreshSession(sessionKey: string): boolean {
  return freshSessionBaseKeys.has(getBaseKeyModule(sessionKey));
}

// ─── 后台空闲断链 ─────────────────────────────────────────────────────────────
// 页面切到后台 30 分钟后，如果当前没有任务在跑，则主动断开 ws；回到前台时再恢复。
// 到点若仍有任务进行中则本次跳过，不在后台循环续 timer。

const BACKGROUND_DISCONNECT_DELAY_MS = 30 * 60 * 1000; // 30分钟
let _backgroundDisconnectTimer: ReturnType<typeof setTimeout> | null = null;
/** 标记当前 ws 是否由后台空闲触发的主动断开，便于回到前台时自动恢复 */
let _isBackgroundSuspended = false;

/** 派生当前是否仍需保持 ws 在线
 *  - _activeRuns 中有尚未收到 final/done 的 run
 *  - Claw 整体仍在 OPENING（沙盒初始化）阶段
 */
function shouldKeepClawWsConnected(): boolean {
  if (_activeRuns.size > 0) return true;
  if (useClawStatus.getState().status === ClawStatus.OPENING) return true;
  return false;
}

function isBackgroundSuspended(): boolean {
  return (
    _isBackgroundSuspended &&
    typeof document !== 'undefined' &&
    document.visibilityState === 'hidden'
  );
}

function getOrCreateClient(): ClawWsClient {
  if (isBackgroundSuspended()) {
    throw new Error('[ClawWs] Background suspended');
  }

  if (!_client) {
    _client = new ClawWsClient({
      url: WS_URL,
      clientId: 'openclaw-macos',
      autoReconnect: true,
      maxReconnectAttempts: 5,
      scopes: ['operator.read', 'operator.write', 'operator.admin'],
    });
  }
  return _client;
}

/**
 * 订阅 Gateway 事件，返回取消订阅函数。
 * 提取为模块级函数，避免 TSX 中 useCallback 内无法写泛型参数的问题。
 */
function subscribeEvent<E extends GatewayEvent>(
  event: E,
  handler: (payload: EventPayloads[E]) => void,
): () => void {
  const client = getOrCreateClient();
  client.on(event, handler);
  return () => client.off(event, handler);
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useClawWs() {
  const {
    connStatus,
    helloOk,
    sessions,
    currentSessionKey,
    messages,
    setConnStatus,
    setHelloOk,
    setSessions,
    setCurrentSessionKey,
    markSessionUnread,
    markSessionRead,
    appendMessage,
    clearMessages,
    clearToolCalls,
    clearLifecycleEvents,
  } = useClawWsStore();

  // ── 连接 ────────────────────────────────────────────────────────────────────

  const connect = useCallback(async () => {
    const store = useClawWsStore.getState();
    if (store.connStatus === 'connected' || store.connStatus === 'connecting')
      return;
    // 后台主动断链期间，避免组件重新挂载又把 ws 拉起，等回到前台再恢复
    if (isBackgroundSuspended()) {
      return;
    }

    const client = getOrCreateClient();

    // 只在首次调用时注册事件监听，避免重复
    // 使用模块级变量而非 useRef，防止组件多次挂载/卸载时重复注册到同一 _client 单例
    if (!_listenersRegistered) {
      _listenersRegistered = true;

      client.onClient('connected', (hello) => {
        useClawWsStore.getState().setConnStatus('connected');
        useClawWsStore.getState().setHelloOk(hello);
      });

      client.onClient('disconnected', () => {
        useClawWsStore.getState().setConnStatus('disconnected');
        useClawWsStore.getState().setHelloOk(null);
      });

      client.onClient('error', () => {
        useClawWsStore.getState().setConnStatus('disconnected');
      });

      // 监听 agent 事件（lifecycle / tool / assistant / error）
      client.on('agent', (payload: AgentEventPayload) => {
        const { runId, stream, ts, data, sessionKey } = payload;
        if (!sessionKey) return;

        if (stream === 'lifecycle') {
          const lcData = data as { phase?: string; startedAt?: number };
          if (lcData.phase === 'start') {
            useClawWsStore.getState().appendLifecycleEvent({
              runId,
              sessionKey,
              phase: 'start',
              startedAt: lcData.startedAt ?? ts,
            });
          }
        } else if (stream === 'tool') {
          const toolData = data as unknown as ToolData;
          if (toolData.phase === 'start') {
            const d = toolData as ToolStartData;
            useClawWsStore.getState().appendToolCall({
              toolCallId: d.toolCallId,
              name: d.name,
              sessionKey,
              runId,
              args: d.args,
              status: 'running',
              startedAt: ts,
            });
          } else if (toolData.phase === 'result') {
            const d = toolData as ToolResultData;
            useClawWsStore.getState().updateToolCall(d.toolCallId, {
              status: d.isError ? 'error' : 'done',
              isError: d.isError,
              result: d.result,
              meta: d.meta,
              finishedAt: ts,
            });
          }
        }
      });

      // 监听 chat 事件，实时写入 store
      client.on('chat', (payload: ChatEventPayload) => {
        const {
          sessionKey: serverKey,
          state,
          message,
          idempotencyKey,
          runId,
        } = payload;
        if (!message) return;

        // 服务端可能对客户端生成的 UUID 加前缀（如 'agent:<main | [agentId]>:<sessionBaseKey[uuid]>'）
        // 首次收到不匹配的 key 时，将 store 中所有消息和 currentSessionKey 同步为服务端格式
        const store = useClawWsStore.getState();
        const clientKey = store.currentSessionKey;
        if (
          clientKey &&
          clientKey !== serverKey &&
          serverKey.endsWith(`:${clientKey}`)
        ) {
          store.remapSessionKey(clientKey, serverKey);
          store.setCurrentSessionKey(serverKey);
        }

        const sessionKey = serverKey;

        // 校验该会话是否已在本地列表中，若无则说明是其他渠道新建的会话，静默刷新列表
        const sessionBaseKey = getBaseKeyModule(serverKey);
        const knownSessions = useClawWsStore.getState().sessions;
        if (
          knownSessions.length > 0 &&
          !knownSessions.some(
            (s) => getBaseKeyModule(s.key) === sessionBaseKey,
          ) &&
          !_pendingSessionRefreshKeys.has(sessionBaseKey)
        ) {
          _pendingSessionRefreshKeys.add(sessionBaseKey);
          void getOrCreateClient()
            .request('sessions.list', {})
            .then((result) => {
              const list =
                (result as { sessions?: ClawSession[] }).sessions ?? [];
              useClawWsStore.getState().setSessions(list);
            })
            .catch(() => {})
            .finally(() => {
              _pendingSessionRefreshKeys.delete(sessionBaseKey);
            });
        }

        const msgRole = (message.role ??
          'assistant') as ClawChatMessage['role'];
        const isDelta = state === 'delta';

        if (isDelta) {
          // 流式分片：打印 content 块类型分布，确认是否有 thinking 块
          const blocks = message.content ?? [];
          const types = blocks.map((b) => b.type);
          const hasThinking = types.includes('thinking');
          if (hasThinking) {
            console.log('[chat delta] thinking block detected:', blocks);
          } else {
            // 只在有非 text 类型时打印，避免刷屏
            const nonText = types.filter((t) => t !== 'text');
            if (nonText.length > 0) {
              console.log('[chat delta] non-text blocks:', nonText, blocks);
            }
          }
          const deltaText = blocks
            .filter((b) => b.type === 'text')
            .map((b) => b.text ?? '')
            .join('');
          useClawWsStore.getState().appendDelta(sessionKey, deltaText, false);
        } else if (state === 'final') {
          // final 是服务端推送的最终权威完整内容，始终强制替换，不做重复检测
          // （可能在 done 之后到达，此时占位已被替换，finishMessage 会更新已有的 done 消息）
          if (idempotencyKey) _activeRuns.delete(idempotencyKey);
          const msg: ClawChatMessage = {
            role: msgRole,
            sessionKey,
            content: (message.content ?? []) as ClawChatMessage['content'],
            done: true,
            timestamp: message.timestamp,
            idempotencyKey: idempotencyKey ?? runId,
          };
          useClawWsStore.getState().finishMessage(msg);
        } else {
          // 其他完整状态（如 done）：防止重复追加
          if (idempotencyKey) _activeRuns.delete(idempotencyKey);
          const lastMsg = useClawWsStore
            .getState()
            .messages.find(
              (m) => m.done && m.idempotencyKey === idempotencyKey,
            );
          if (lastMsg) {
            return;
          }
          const msg: ClawChatMessage = {
            role: msgRole,
            sessionKey,
            content: (message.content ?? []) as ClawChatMessage['content'],
            done: true,
            timestamp: message.timestamp,
            idempotencyKey: idempotencyKey ?? runId,
          };
          useClawWsStore.getState().finishMessage(msg);
        }
      });
    }

    setConnStatus('connecting');
    try {
      await client.connect();
    } catch {
      setConnStatus('disconnected');
    }
  }, [setConnStatus]);

  const disconnect = useCallback(() => {
    _client?.disconnect();
    _client = null;
    _listenersRegistered = false;
    setConnStatus('disconnected');
    setHelloOk(null);
  }, [setConnStatus, setHelloOk]);

  // ── 会话 ────────────────────────────────────────────────────────────────────

  /** 拉取会话列表并同步到 store */
  const fetchSessions = useCallback(async (): Promise<ClawSession[]> => {
    const client = getOrCreateClient();
    const result = await client.request('sessions.list', {});
    const list: ClawSession[] =
      (result as { sessions?: ClawSession[] }).sessions ?? [];
    const prevList = useClawWsStore
      .getState()
      .sessions.filter((s) => !isMarioWebchatSessionKey(s.key));
    const activeKey = useClawWsStore.getState().currentSessionKey;
    // 首次拉取不打红点；后续仅对新增会话或更新时间变更的非当前会话打未读。
    if (prevList.length > 0) {
      const prevTsMap = new Map<string, number>();
      prevList.forEach((s) => {
        prevTsMap.set(s.key, s.updatedAt ?? s.createdAt ?? 0);
      });
      list.forEach((s) => {
        if (s.key === activeKey) return;
        const nextTs = s.updatedAt ?? s.createdAt ?? 0;
        const prevTs = prevTsMap.get(s.key);
        if (
          !isMarioWebchatSessionKey(s.key) &&
          (prevTs === undefined || nextTs > prevTs)
        ) {
          markSessionUnread(s.key);
        }
      });
    }
    const newList = [
      ...useClawWsStore
        .getState()
        .sessions.filter((s) => isMarioWebchatSessionKey(s.key)),
      ...list,
    ].sort((a, b) => {
      return (
        Number(b.updatedAt ?? b.createdAt ?? 0) -
        Number(a.updatedAt ?? a.createdAt ?? 0)
      );
    });
    setSessions(newList);
    if (activeKey) {
      markSessionRead(activeKey);
    }

    // 对没有 label 的会话自动生成标题（fire-and-forget）
    list
      .filter((s) => !s.label)
      .forEach((s) => autoLabelSession(s.key).catch(() => {}));

    return list;
  }, [setSessions, markSessionUnread, markSessionRead]);

  /** 切换当前会话 */
  const switchSession = useCallback(
    (key: string) => {
      if (key === '') {
        setCurrentSessionKey(null);
        clearMessages();
        clearToolCalls();
        clearLifecycleEvents();
        freshSessionBaseKeys.clear();
        return;
      }
      if (
        currentSessionKey &&
        (key === currentSessionKey ||
          getBaseKeyModule(key) === getBaseKeyModule(currentSessionKey))
      )
        return;
      setCurrentSessionKey(key);
      clearMessages();
      clearToolCalls();
      clearLifecycleEvents();
      if (currentSessionKey && isFreshSession(currentSessionKey)) {
        freshSessionBaseKeys.delete(getBaseKeyModule(currentSessionKey ?? ''));
      }
    },
    [
      setCurrentSessionKey,
      clearMessages,
      clearToolCalls,
      clearLifecycleEvents,
      currentSessionKey,
    ],
  );

  /** 重置会话（清空上下文） */
  const resetSession = useCallback(async (sessionKey: string) => {
    const client = getOrCreateClient();
    await client.request('sessions.patch', { key: sessionKey });
  }, []);

  /** 重命名会话 label */
  const renameSession = useCallback(
    async (key: string, label: string) => {
      const client = getOrCreateClient();
      await client.request('sessions.patch', { key, label });
      setSessions(
        useClawWsStore
          .getState()
          .sessions.map((s) => (s.key === key ? { ...s, label } : s)),
      );
    },
    [setSessions],
  );

  /** 删除会话 */
  const deleteSession = useCallback(
    async (key: string) => {
      const client = getOrCreateClient();
      await client.request('sessions.delete', { key });
      setSessions(
        useClawWsStore.getState().sessions.filter((s) => s.key !== key),
      );
      if (useClawWsStore.getState().currentSessionKey === key) {
        setCurrentSessionKey(null);
        clearMessages();
        clearToolCalls();
        clearLifecycleEvents();
      }
    },
    [
      setSessions,
      setCurrentSessionKey,
      clearMessages,
      clearToolCalls,
      clearLifecycleEvents,
    ],
  );

  // ── 聊天 ────────────────────────────────────────────────────────────────────

  /**
   * 发送一条消息
   * - 立即将用户消息 + 空 assistant 占位写入 store
   * - 通过 chat 事件流式更新 assistant 内容
   * - onChunk 可选，用于额外的流式回调
   */
  const sendMessage = useCallback(
    async (
      content: string,
      sessionKey?: string,
      onChunk?: (chunk: ChatEventPayload) => void,
      attachments?: Attachment[],
      agentKey?: string,
    ): Promise<string> => {
      const client = getOrCreateClient();
      const prevKey = useClawWsStore.getState().currentSessionKey;
      const key = agentKey ?? sessionKey ?? prevKey ?? uuidv4();
      const idempotencyKey = uuidv4();

      // 首次发送（没有已有 session），激活新会话
      if (!prevKey && (!sessionKey || agentKey)) {
        setCurrentSessionKey(key);
        clearMessages();
        clearToolCalls();
        clearLifecycleEvents();
        // 标记为本次页面生命周期内新建的 session，MessageList 会跳过历史拉取
        freshSessionBaseKeys.add(getBaseKeyModule(key));
        // 与非 Claw 模式一致：发送后先本地插入会话，避免必须等待首轮回复完成才出现在历史列表。
        const nowSec = Math.floor(Date.now() / 1000);
        const optimisticLabel = content.trim().slice(0, 30);
        const prevSessions = useClawWsStore.getState().sessions;
        if (!prevSessions.some((s) => s.key === key)) {
          setSessions([
            {
              key,
              label: optimisticLabel || '新会话',
              createdAt: nowSec,
              updatedAt: nowSec,
            },
            ...prevSessions,
          ]);
        }
      }
      // 注意：不在新 run 开始时 clearToolCalls；
      // 各轮 tool calls 按 runId 分组保留，切换会话时统一清空。

      // 标记该 run 为活跃，直到收到服务端 final/done 才清除
      _activeRuns.add(idempotencyKey);

      // 立即写入用户消息（附件仅存元数据，不含 base64 content）
      appendMessage({
        role: 'user',
        sessionKey: key,
        content,
        done: true,
        idempotencyKey: idempotencyKey + '-user',
        attachments: attachments?.map(({ content: _c, ...meta }) => meta),
      });

      // 占位 assistant 消息（流式写入）
      appendMessage({
        role: 'assistant',
        sessionKey: key,
        content: '',
        done: false,
        idempotencyKey,
      });

      const params: ChatSendParams = {
        idempotencyKey,
        sessionKey: key,
        message: content,
        deliver: true,
        ...(attachments?.length ? { attachments } : {}),
      };

      try {
        await client.sendChat(params, onChunk);
      } catch (err) {
        _activeRuns.delete(idempotencyKey);
        throw err;
      }

      // 刷新会话列表（新会话会在这时生成）
      await fetchSessions().catch(() => {});
      // 首条消息自动生成标题：fire-and-forget，不阻塞发送流程（含显式 agent sessionKey 的首发）
      if (!prevKey && (!sessionKey || agentKey)) {
        sessionsSummarize({ content, size: 30 })
          .then((res) => {
            const title = res.data?.title;
            if (!title) return;
            // key 可能已被服务端重映射（加 agent:main: 前缀），取最新值
            const finalKey = useClawWsStore.getState().currentSessionKey ?? key;
            return client.request('sessions.patch', {
              key: finalKey,
              label: title,
            });
          })
          .then(() => fetchSessions().catch(() => {}))
          .catch(() => {});
      }

      return key;
    },
    [
      appendMessage,
      fetchSessions,
      setCurrentSessionKey,
      clearMessages,
      clearToolCalls,
      clearLifecycleEvents,
    ],
  );

  /** 中断当前正在运行的回复 */
  const abortChat = useCallback(async (sessionKey: string) => {
    const client = getOrCreateClient();
    await client.request('chat.abort', { sessionKey });
  }, []);

  /** 拉取指定会话的历史消息 */
  const fetchHistory = useCallback(async (sessionKey: string, limit = 20) => {
    const client = getOrCreateClient();
    return client.request('chat.history', { sessionKey, limit });
  }, []);

  /** 拉取网关日志（调试用） */
  const fetchLogs = useCallback(async (_lines = 100) => {
    const client = getOrCreateClient();
    return client.request('logs.tail', {});
  }, []);

  /** 订阅所有原始 WS 帧（调试用），返回取消订阅函数 */
  const subscribeFrames = useCallback((handler: RawFrameHandler) => {
    const client = getOrCreateClient();
    client.onRawFrame(handler);
    return () => client.offRawFrame(handler);
  }, []);

  // ── 其他常用方法 ─────────────────────────────────────────────────────────────

  /** 健康检查 */
  const health = useCallback(async () => {
    const client = getOrCreateClient();
    return client.request('health', {});
  }, []);

  /** 获取 Agent 列表 */
  const fetchAgents = useCallback(async () => {
    const client = getOrCreateClient();
    return client.request('agents.list', {});
  }, []);

  /** 获取节点列表 */
  const fetchNodes = useCallback(async () => {
    const client = getOrCreateClient();
    return client.request('node.list', {});
  }, []);

  // subscribe 直接指向模块级泛型函数，无需 useCallback 包裹
  // （模块级函数引用稳定，不会引起不必要的重渲染）
  const subscribe = subscribeEvent;

  // ── 自动连接 ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    connect();
  }, [connect]);

  // ── 后台空闲断链 ────────────────────────────────────────────────────────────
  // 页面进入后台启动timer；到点时若仍在后台且无活跃任务则主动断开，
  // 回到前台后再自动恢复。timer / suspended 标记为模块级单例，多组件共享。

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const clearBackgroundTimer = () => {
      if (_backgroundDisconnectTimer !== null) {
        clearTimeout(_backgroundDisconnectTimer);
        _backgroundDisconnectTimer = null;
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        clearBackgroundTimer();
        _backgroundDisconnectTimer = setTimeout(() => {
          _backgroundDisconnectTimer = null;
          if (document.visibilityState !== 'hidden') return;
          if (shouldKeepClawWsConnected()) return;
          // 复用 ClawWsClient.disconnect 的语义：停止 heartbeat、关闭 socket、关闭 autoReconnect
          _isBackgroundSuspended = true;
          _client?.disconnect();
          _client = null;
          _listenersRegistered = false;
          const store = useClawWsStore.getState();
          store.setConnStatus('disconnected');
          store.setHelloOk(null);
        }, BACKGROUND_DISCONNECT_DELAY_MS);
        return;
      }
      clearBackgroundTimer();
      if (_isBackgroundSuspended) {
        _isBackgroundSuspended = false;
        void connect();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    // 挂载时若已处于后台，立刻按一次后台逻辑启动 timer
    if (document.visibilityState === 'hidden') {
      handleVisibility();
    }
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [connect]);

  // ─────────────────────────────────────────────────────────────────────────────

  return {
    // State（来自 store，响应式）
    connStatus,
    helloOk,
    sessions,
    currentSessionKey,
    messages,
    isConnected: connStatus === 'connected',

    // 连接
    connect,
    disconnect,

    // 会话
    fetchSessions,
    switchSession,
    resetSession,
    renameSession,
    deleteSession,

    // 聊天
    sendMessage,
    abortChat,
    fetchHistory,
    fetchLogs,
    subscribeFrames,

    // 其他
    health,
    fetchAgents,
    fetchNodes,
    subscribe,

    // 直接暴露客户端实例，用于需要精细控制的场景
    get client() {
      return _client;
    },
  };
}
